import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import {
  Article as ArticleIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  GridView as GridViewIcon,
  Group as GroupIcon,
  Logout as LogoutIcon,
  MonetizationOn as MonetizationOnIcon,
  NoAccounts as NoAccountsIcon,
  PlayArrow,
  PrivacyTip as PrivacyTipIcon,
  Subscriptions as SubscriptionsIcon,
} from "@mui/icons-material";
import { MenuIcon } from "./../../assets/images";
import { LOGO_URL } from "../../utils/constant";
import { useNavigate } from "react-router-dom";
import Logout from "../../shared/Modals/Logout";

const drawerWidth = 240;
const mobileDrawerWidth = 296;

const primaryNavItems = [
  {
    name: "Dashboard",
    path: "/",
    icon: <GridViewIcon />,
  },
  {
    name: "Users",
    path: "/users",
    icon: <GroupIcon />,
  },
  {
    name: "Appointments",
    path: "/appointments",
    icon: <NoAccountsIcon />,
  },
  {
    name: "Offers",
    path: "/offer",
    icon: <MonetizationOnIcon />,
  },
  {
    name: "Blogs",
    path: "/blog",
    icon: <SubscriptionsIcon />,
  },
  {
    name: "Coupons",
    path: "/coupon",
    icon: <SubscriptionsIcon />,
  },
  {
    name: "Shops & Operat. Hours",
    path: "/shop",
    icon: <ArticleIcon />,
  },
];

const settingsNavItems = [
  {
    name: "Add/Remove Rewards",
    path: "/firsReward",
    icon: <PlayArrow />,
  },
  {
    name: "Dynamic Links",
    path: "/dynamiclinks",
    icon: <PlayArrow />,
  },
  {
    name: "Onboarding Images",
    path: "/onboarding",
    icon: <PlayArrow />,
  },
  {
    name: "Reward amounts",
    path: "/reward",
    icon: <PlayArrow />,
  },
  {
    name: "System Logo",
    path: "/systemLogo",
    icon: <PlayArrow />,
  },
  {
    name: "Media Upload",
    path: "/media",
    icon: <PlayArrow />,
  },
];

const settingsPaths = new Set(settingsNavItems.map((item) => item.path));

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const navButtonSx = (selected, showLabels) => ({
  minHeight: 48,
  justifyContent: showLabels ? "initial" : "center",
  px: 2.5,
  borderRadius: "12px",
  backgroundColor: selected ? "rgb(248, 61, 75)" : "transparent",
  "&:hover": {
    backgroundColor: selected ? "rgb(248, 61, 75)" : "rgba(248, 61, 75, 0.08)",
  },
});

const navIconSx = (selected, showLabels) => ({
  minWidth: 0,
  mr: showLabels ? 3 : "auto",
  justifyContent: "center",
  color: selected ? "#FFF" : "rgb(128, 129, 145)",
});

const navTextSx = (selected, showLabels) => ({
  opacity: showLabels ? 1 : 0,
  color: selected ? "#FFF" : "rgb(128, 129, 145)",
  "& .MuiTypography-root": {
    fontWeight: 600,
  },
});

function DrawerHeader({ showLogo, onToggle, mobile }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 1,
        py: mobile ? 1.25 : 1,
        minHeight: 64,
      }}
    >
      {showLogo ? (
        <Box
          component="img"
          src={LOGO_URL}
          alt="Integrity 1st Car Pros"
          sx={{
            maxWidth: mobile ? "72%" : "85%",
            height: "auto",
            ml: mobile ? 1 : 0.5,
          }}
        />
      ) : (
        <Box />
      )}

      <IconButton onClick={onToggle} aria-label={mobile ? "Close navigation" : "Toggle navigation"}>
        {mobile ? <CloseIcon /> : <img src={MenuIcon} alt="" />}
      </IconButton>
    </Box>
  );
}

function AdminNavigation({
  currentPath,
  onNavigate,
  onLogout,
  settingsOpen,
  onSettingsToggle,
  showLabels,
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <List sx={{ flexGrow: 1, px: 1 }}>
        <Box>
          {primaryNavItems.map((item) => {
            const selected = item.path === currentPath;

            return (
              <ListItem key={item.path} disablePadding sx={{ display: "block", mb: 0.5 }}>
                <ListItemButton
                  selected={selected}
                  sx={navButtonSx(selected, showLabels)}
                  onClick={() => onNavigate(item.path)}
                >
                  <ListItemIcon sx={navIconSx(selected, showLabels)}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    className="listItems"
                    sx={navTextSx(selected, showLabels)}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </Box>

        <Box sx={{ mt: 1 }}>
          <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
            <ListItemButton
              sx={{
                ...navButtonSx(false, showLabels),
                "&:hover": {
                  backgroundColor: "rgba(248, 61, 75, 0.08)",
                },
              }}
              onClick={onSettingsToggle}
            >
              <ListItemIcon sx={navIconSx(false, showLabels)}>
                <PrivacyTipIcon />
              </ListItemIcon>
              <ListItemText
                primary="System Settings"
                className="listItems"
                sx={navTextSx(false, showLabels)}
              />
              <Box sx={{ color: "rgb(128, 129, 145)", ml: showLabels ? 1 : 0 }}>
                {settingsOpen ? <ExpandLess /> : <ExpandMore />}
              </Box>
            </ListItemButton>
          </ListItem>

          <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {settingsNavItems.map((item) => {
                const selected = item.path === currentPath;

                return (
                  <ListItem key={item.path} disablePadding sx={{ display: "block", mb: 0.5 }}>
                    <ListItemButton
                      sx={{
                        ...navButtonSx(selected, showLabels),
                        pl: showLabels ? 4 : 2.5,
                      }}
                      onClick={() => onNavigate(item.path)}
                    >
                      <ListItemIcon sx={navIconSx(selected, showLabels)}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.name} sx={navTextSx(selected, showLabels)} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </Box>
      </List>

      <Box sx={{ px: 1, pb: 1 }}>
        <ListItem disablePadding sx={{ display: "block" }}>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: showLabels ? "initial" : "center",
              px: 2.5,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(255, 101, 101, 0.1)",
              },
            }}
            onClick={onLogout}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: showLabels ? 3 : "auto",
                justifyContent: "center",
                color: "#ff6565",
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              className="listItems"
              sx={{
                opacity: showLabels ? 1 : 0,
                color: "#ff6565",
                "& .MuiTypography-root": {
                  fontWeight: 600,
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );
}

export default function MiniDrawer(props) {
  const isMobile = useMediaQuery("(max-width:767px)");
  const navigate = useNavigate();
  const PageComponent = props.component;
  const activeSettingsPath = useMemo(
    () => settingsPaths.has(props.path),
    [props.path]
  );

  const [desktopOpen, setDesktopOpen] = useState(true);
  const [desktopSettingsOpen, setDesktopSettingsOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(activeSettingsPath);
  const [showLogout, setShowLogout] = useState(false);

  useEffect(() => {
    if (activeSettingsPath) {
      setDesktopSettingsOpen(true);
      setMobileSettingsOpen(true);
    }
  }, [activeSettingsPath]);

  const handleNavigate = (path) => {
    navigate(path);

    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleOpenLogout = () => {
    setShowLogout(true);

    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleCloseLogout = () => {
    setShowLogout(false);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", width: "100%" }}>
      <CssBaseline />

      {isMobile ? (
        <>
          <MuiDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: mobileDrawerWidth,
              },
            }}
            // The legacy MUI v4 (@material-ui/core) + v5 (@mui/material) deps
            // both pull react-transition-group, so the Drawer's Slide/Fade
            // *enter* transitions never run — the paper stays stuck off-canvas
            // (translateX) and the scrim stays at opacity 0. When open, force
            // the paper on-screen and the backdrop visible via stylesheet
            // !important (which beats MUI's inline transition styles); the CSS
            // transition still animates the slide-in / fade. Remove once the
            // duplicate MUI v4 dependency is dropped (see CLAUDE.md P3.8).
            sx={
              mobileOpen
                ? {
                    "& .MuiDrawer-paper": { transform: "none !important" },
                    "& .MuiBackdrop-root": { opacity: "1 !important" },
                  }
                : undefined
            }
          >
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <DrawerHeader
                mobile
                showLogo
                onToggle={() => setMobileOpen(false)}
              />
              <Divider />
              <AdminNavigation
                currentPath={props.path}
                onNavigate={handleNavigate}
                onLogout={handleOpenLogout}
                settingsOpen={mobileSettingsOpen}
                onSettingsToggle={() => setMobileSettingsOpen((prev) => !prev)}
                showLabels
              />
            </Box>
          </MuiDrawer>

          <Box sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}>
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: (theme) => theme.zIndex.appBar,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                py: 1.5,
                backgroundColor: "#FFF",
                borderBottom: "1px solid #E4E4E4",
              }}
            >
              <IconButton onClick={() => setMobileOpen(true)} aria-label="Open navigation">
                <img src={MenuIcon} alt="" />
              </IconButton>

              <Box
                component="img"
                src={LOGO_URL}
                alt="Integrity 1st Car Pros"
                sx={{
                  maxWidth: "62%",
                  height: "auto",
                }}
              />
            </Box>

            <Box
              component="main"
              sx={{
                flexGrow: 1,
                p: 2,
                minWidth: 0,
                width: "100%",
                overflowX: "hidden",
              }}
            >
              <PageComponent open={mobileOpen} />
            </Box>
          </Box>
        </>
      ) : (
        <>
          <DesktopDrawer
            variant="permanent"
            open={desktopOpen}
            className="drawerBackground"
          >
            <DrawerHeader
              showLogo={desktopOpen}
              onToggle={() => setDesktopOpen((prev) => !prev)}
            />

            <List sx={{ height: "88vh" }}>
              <AdminNavigation
                currentPath={props.path}
                onNavigate={handleNavigate}
                onLogout={handleOpenLogout}
                settingsOpen={desktopSettingsOpen}
                onSettingsToggle={() => setDesktopSettingsOpen((prev) => !prev)}
                showLabels={desktopOpen}
              />
            </List>
          </DesktopDrawer>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              minWidth: 0,
              overflowX: "hidden",
              overflowY: "auto",
            }}
          >
            <PageComponent open={desktopOpen} />
          </Box>
        </>
      )}

      <Logout
        open={showLogout}
        handleClickOpen={handleOpenLogout}
        handleClose={handleCloseLogout}
      />
    </Box>
  );
}
